/**
 * 色彩范围对话框（选择→色彩范围…，DESIGN.md 对比 Photoshop 补全）。
 *
 * 参考色由"点击画布取样"产生，不在这个组件内部——取样发生在画布的
 * 指针事件里（main.tsx 判断"色彩范围对话框开着"时把点击路由成取样，
 * 不是走平常的工具分发），所以 `sampledColor` 是外部传入的受控值，不是
 * 组件自己的 state。容差/反相则是这个对话框自己的本地状态（跟
 * SelectionModifyDialog 的 amount 一个惯例），但每次变化都要通知外部
 * 重新计算——色彩范围是"边调边看"的对话框（真实 Photoshop 也是拖动
 * 容差滑杆时选区实时更新），不是填完参数再一次性生成。
 *
 * 简化（已在复盘记录披露）：只支持单点取样，没有 Shift/Alt 追加/减去
 * 取样点，也没有"红色/黄色/高光/中间调/肤色"这类预设。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './ColorRangeDialog.module.css';

export interface ColorRangeParams {
  readonly fuzziness: number;
  readonly invert: boolean;
}

export interface ColorRangeDialogProps {
  readonly open: boolean;
  /** 目前取样到的参考色（`#rrggbb`），还没点过画布时是 null。 */
  readonly sampledColor: string | null;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  /** 容差/反相任意一项变化都会调用——外部据此重新计算实时预览的选区。 */
  readonly onChange: (params: ColorRangeParams) => void;
}

export const COLOR_RANGE_DEFAULT_FUZZINESS = 40;

export function ColorRangeDialog(props: ColorRangeDialogProps): JSX.Element {
  const [fuzziness, setFuzziness] = createSignal(COLOR_RANGE_DEFAULT_FUZZINESS);
  const [invert, setInvert] = createSignal(false);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  // 每次打开都重置成默认值——组件实例贯穿应用生命周期，上一次关闭时的
  // 选择不该带到下一次
  createEffect(() => {
    if (props.open) {
      setFuzziness(COLOR_RANGE_DEFAULT_FUZZINESS);
      setInvert(false);
    }
  });

  return (
    <Show when={props.open}>
      <div
        class={styles.dialog}
        role="dialog"
        ref={(el) => (dialogRef = el)}
        aria-label="色彩范围"
        data-testid="color-range-dialog"
      >
        <h2 class={styles.title}>色彩范围</h2>
        <p class={styles.hint}>点击画布取样参考色</p>

        <div class={styles.row}>
          <span class={styles.label}>取样颜色</span>
          <div
            class={`${styles.swatch} ${props.sampledColor === null ? styles.swatchEmpty : ''}`}
            style={props.sampledColor !== null ? { background: props.sampledColor } : undefined}
            data-testid="color-range-swatch"
          />
        </div>

        <div class={styles.row}>
          <span class={styles.label}>容差</span>
          <input
            type="range"
            class={styles.slider}
            min="1"
            max="255"
            step="1"
            value={fuzziness()}
            data-testid="color-range-fuzziness-slider"
            onInput={(event) => {
              const value = Number(event.currentTarget.value);
              setFuzziness(value);
              props.onChange({ fuzziness: value, invert: invert() });
            }}
          />
          <input
            type="number"
            class={styles.number}
            min="1"
            max="255"
            value={fuzziness()}
            data-testid="color-range-fuzziness-number"
            onInput={(event) => {
              const value = Math.min(255, Math.max(1, Math.round(Number(event.currentTarget.value) || 1)));
              setFuzziness(value);
              props.onChange({ fuzziness: value, invert: invert() });
            }}
          />
        </div>

        <label class={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={invert()}
            data-testid="color-range-invert"
            onChange={(event) => {
              const value = event.currentTarget.checked;
              setInvert(value);
              props.onChange({ fuzziness: fuzziness(), invert: value });
            }}
          />
          反相
        </label>

        <div class={styles.actions}>
          <button type="button" class={styles.secondary} data-testid="color-range-cancel" onClick={props.onClose}>
            取消
          </button>
          <button type="button" class={styles.primary} data-testid="color-range-confirm" onClick={props.onConfirm}>
            确定
          </button>
        </div>
      </div>
    </Show>
  );
}
